import apiClient from '@app/auth/auth-api-client'
import { iframeAuthProxy } from './frame-proxy'

const userBackendApi = apiClient(IDENTITY_SERVICE_URL)
const lenderBackendApi = apiClient(API2_URL)

/**
 * Unwrap a proxy response, throwing on failure to match axios behavior.
 * @param {Object} result - The proxy response { success, status, statusText, data, error }
 * @returns {*} result.data on success
 * @throws {Error} on failure
 */
function unwrapProxyResponse(result) {
  if (!result.success) {
    const message = result.error || result.data?.detail || result.data?.error || `Proxy request failed with status ${result.status}`
    const error = new Error(message)
    error.status = result.status
    error.data = result.data
    throw error
  }
  return result.data
}

export const userServiceEndpoints = {
  /**
   * @param {string} username - The username (email) for login
   * @param {string} password - The password for login
   * @param {string} lender_id - The lender account id for login
   * @returns {Promise<{access_token: string, expires: number}>} New bearer token with expiration
   */
  LOGIN: async (username, password, lender_id) => {
    if (!lender_id) {
      throw new Error('Account selection required')
    }
    if (!username || !password) {
      throw new Error('Username and password are required')
    }

    const result = await iframeAuthProxy.operation('auth:login', {
      body: { username, password, lender_id, client_id: IDENTITY_CLIENT_ID },
    })
    return unwrapProxyResponse(result)
  },
  /**
   * @param {string|undefined} loginCode - The login code to establish a session
   * @returns {Promise<{access_token: string, expires: number}>} New bearer token with expiration
   */
  LOGIN_CODE: async (loginCode) => {
    if (!loginCode) {
      throw new Error('Login code is required')
    }

    const result = await iframeAuthProxy.operation('auth:login-code', {
      body: { code: loginCode, client_id: IDENTITY_CLIENT_ID },
    })
    return unwrapProxyResponse(result)
  },
  /**
   * @param {string|undefined} loginToken - The bearer token for login
   * @returns {Promise<{access_token: string, expires: number}>} The existing bearer token with expiration
   */
  LOGIN_TOKEN: async (loginToken) => {
    if (!loginToken) {
      throw new Error('Login token is required')
    }

    const result = await iframeAuthProxy.operation('auth:login-token', {
      body: { token: loginToken, client_id: IDENTITY_CLIENT_ID },
    })
    return unwrapProxyResponse(result)
  },
  /**
   * @param {string|undefined} currentBearerToken - The current bearer token for authorization
   * @returns {Promise<null>} Returns nothing
   */
  LOGOUT: async (currentBearerToken) => {
    const headers = {}
    if (currentBearerToken) {
      headers.Authorization = `Bearer ${currentBearerToken}`
    }

    const result = await iframeAuthProxy.operation('auth:logout', { headers })
    unwrapProxyResponse(result)
  },
  /**
   * Get the authentication status and token information
   * @param {string|undefined} currentBearerToken - The current bearer token for authorization
   * @returns {Promise<{access_token: string, expires: number}>} A valid bearer token with expiration
   */
  STATUS: async (currentBearerToken) => {
    const headers = {}
    if (currentBearerToken) {
      headers.Authorization = `Bearer ${currentBearerToken}`
    }

    const result = await iframeAuthProxy.operation('auth:status', { headers })
    return unwrapProxyResponse(result)
  },
  /**
   * Get new bearer token for session
   * @returns {Promise<{access_token: string, expires: number}>} The new bearer token with expiration
   */
  TOKEN: async () => {
    const result = await iframeAuthProxy.operation('auth:token')
    return unwrapProxyResponse(result)
  },
  /**
   * Get accounts associated with email
   * @param {string} email - The email to look up accounts for
   * @returns {Promise<{id: string, name: string, logo_url: string}[]>} The account options available for login
   */
  ACCOUNT_OPTIONS: async (email) => {
    const lenderAccounts = await userBackendApi.post('/lender/accounts', {}, { email: email })
    return lenderAccounts
  },
  /**
   * Get login methods for account
   * @param {string} accountId - The account id to look up login methods for
   * @returns {Promise<{type: string, url?: string}[]>} The login methods available for the account
   */
  ACCOUNT_LOGIN_METHODS: async (accountId) => {
    const lenderLoginMethods = await userBackendApi.post(`/lender/accounts/${accountId}/login_methods`, {}, {})
    return lenderLoginMethods
  },
}

export const lenderBackendEndpoints = {
  CURRENT_USER: async (currentBearerToken) => {
    const headers = {}
    if (currentBearerToken) {
      headers.Authorization = `Bearer ${currentBearerToken}`
    }
    const result = await lenderBackendApi.get('/users/current_user', {}, { headers })
    return result
  },
}
